/**
 * Partner Configuration types matching the database schema.
 * These types mirror the partner_configurations table structure.
 */

// Enums for partner configurations
export type PartnerType = 'ACO' | 'EMPLOYER' | 'MA';

// =============================================================================
// Default Offers - Rewards auto-assigned to new users
// =============================================================================

/**
 * Context JSON structure nested within DefaultOfferConfig
 */
export interface DefaultOfferContextJson {
  /** Source of the assignment (e.g., 'auto_default', 'admin_bulk_assign') */
  source: string;
  /** Reward key (e.g., 'cgm-device') */
  reward_key: string;
  /** Reward variant (e.g., 'default') */
  reward_variant: string;
  /** ISO timestamp when assigned */
  assigned_at: string;
  /** Override availability at user-level (true=grant access, false=block, null=no override) */
  available_override?: boolean | null;
  /** Message to show when override blocks access */
  override_reason?: string | null;
  /** Show override message on reward card in list view */
  show_message_on_card?: boolean | null;
}

/**
 * Default offer configuration (stored in partner_configurations.default_offers)
 * These rewards are automatically assigned to users when they first access the rewards catalog.
 * Structure MUST match existing database records exactly.
 */
export interface DefaultOfferConfig {
  /** UUID of the reward from reward_catalog */
  reward_id: string;
  /** Display order priority (lower = higher priority) */
  display_priority: number;
  /** Context data including reward_key and reward_variant */
  context_json: DefaultOfferContextJson;
  /** Assignment strategy (e.g., 'AUTO_DEFAULT', 'MANUAL') */
  assignment_strategy: string;
}

// =============================================================================
// Bulk Assign Offers - Easy Button for assigning rewards to partner users
// =============================================================================

/**
 * Request to bulk assign an offer to all users of a partner (or all partners)
 */
export interface BulkAssignOfferRequest {
  /** UUID of the reward to assign */
  reward_id: string;
  /** Display priority for the assigned offers */
  display_priority?: number;
  /** Also add this reward to the partner's default_offers configuration */
  add_to_default_offers?: boolean;
  /** Override availability at offer level */
  available_override?: boolean | null;
  /** Message to show when override is active */
  override_reason?: string | null;
  /** Show override message on reward card */
  show_message_on_card?: boolean | null;
}

/**
 * Response from bulk assign offer operation
 */
export interface BulkAssignOfferResponse {
  /** Number of new offers created */
  created: number;
  /** UUID of the reward that was assigned */
  reward_id: string;
  /** Number of partners whose default_offers were updated */
  partners_updated: number;
  /** List of partner_product_ids that were affected */
  partner_product_ids: number[];
}

/**
 * Request to bulk remove an offer from all users of a partner (or all partners)
 */
export interface BulkRemoveOfferRequest {
  /** UUID of the reward to remove */
  reward_id: string;
  /** Also remove this reward from the partner's default_offers configuration */
  remove_from_default_offers?: boolean;
}

/**
 * Response from bulk remove offer operation
 */
export interface BulkRemoveOfferResponse {
  /** Number of offers removed */
  removed: number;
  /** UUID of the reward that was removed */
  reward_id: string;
  /** Number of partners whose default_offers were updated */
  partners_updated: number;
  /** List of partner_product_ids that were affected */
  partner_product_ids: number[];
}

/**
 * Request to bulk replace one reward with another for all users of a partner (or all partners)
 */
export interface BulkReplaceOfferRequest {
  /** UUID of the reward to replace */
  source_reward_id: string;
  /** UUID of the new reward to assign */
  target_reward_id: string;
  /** Display priority (null = keep existing) */
  display_priority?: number | null;
  /** Also update partner's default_offers configuration */
  update_default_offers?: boolean;
  /** Preserve existing context_json fields like overrides */
  preserve_context?: boolean;
  /** Override availability at offer level */
  available_override?: boolean | null;
  /** Message to show when override is active */
  override_reason?: string | null;
  /** Show override message on reward card */
  show_message_on_card?: boolean | null;
}

/**
 * Response from bulk replace offer operation
 */
export interface BulkReplaceOfferResponse {
  /** Number of offers replaced */
  replaced: number;
  /** UUID of the reward that was replaced */
  source_reward_id: string;
  /** UUID of the new reward */
  target_reward_id: string;
  /** Number of partners whose default_offers were updated */
  partners_updated: number;
  /** List of partner_product_ids that were affected */
  partner_product_ids: number[];
}

// =============================================================================
// Content Overrides - Partner-specific content customization
// =============================================================================

/**
 * Custom content override - replaces a specific field value for a partner
 */
export interface CustomContentOverride {
  /** Flat dot notation field path (e.g., 'info_sheet.how_it_works_content_provider') */
  field_path: string;
  /** Override value for this field */
  value: string;
}

/**
 * Content override rules for a specific reward within a partner configuration.
 * Allows partners to hide fields, disable validations, or provide custom content.
 */
export interface RewardContentOverride {
  /** Reward key this override applies to (e.g., 'cgm-device', 'benepass-card') */
  reward_key: string;
  /**
   * List of flat dot notation field paths to hide from the UI.
   * Example: ['amount_selection.help_text_minimum', 'amount_selection.help_text_monthly_max']
   */
  hide_fields: string[];
  /**
   * List of validation types to disable.
   * Example: ['min_amount', 'max_amount', 'monthly_limit', 'yearly_limit']
   */
  hide_validations: string[];
  /**
   * List of custom content overrides - field path and value pairs.
   */
  custom_content: CustomContentOverride[];
}

/**
 * Partner-specific content overrides structure.
 * Stored in partner_configurations.content_overrides column.
 */
export interface PartnerContentOverrides {
  /** List of reward-specific override rules */
  overrides: RewardContentOverride[];
}

/**
 * Known validation types that can be hidden
 */
export const VALIDATION_TYPES = [
  'min_amount',
  'max_amount',
  'max_monthly_amount',
  'monthly_limit',
  'yearly_limit',
] as const;

export type ValidationType = (typeof VALIDATION_TYPES)[number];

/**
 * Helper to create an empty content override for a reward
 */
export function createEmptyContentOverride(rewardKey: string): RewardContentOverride {
  return {
    reward_key: rewardKey,
    hide_fields: [],
    hide_validations: [],
    custom_content: [],
  };
}

/**
 * Helper to create empty partner content overrides
 */
export function createEmptyPartnerContentOverrides(): PartnerContentOverrides {
  return {
    overrides: [],
  };
}

// =============================================================================
// Partner Config JSON - General-purpose configuration flags
// =============================================================================

/**
 * General-purpose partner configuration flags stored in config_json column.
 */
export interface PartnerConfigJson {
  /** Whether fulfillments are exported for processing (default: true) */
  fulfillment_enabled: boolean;
}

// Main partner response matching database columns
export interface PartnerResponse {
  partner_product_id: number; // bigint primary key
  partner_id: number | null; // bigint
  partner_name: string;
  doing_business_as: string;
  partner_type: PartnerType;
  program_code?: string | null;
  package_id?: string | null;
  purse_name?: string | null;
  earned_benefits: string[]; // text array
  /** Default offers auto-assigned to new users */
  default_offers: DefaultOfferConfig[] | null;
  /** Partner-specific content overrides for reward workflows */
  content_overrides: PartnerContentOverrides | null;
  effective_date: string; // ISO date string
  expiration_date: string | null;
  is_active: boolean;
  notes: string | null;
  /** Partner configuration flags (fulfillment_enabled, etc.) */
  config: PartnerConfigJson | null;
  /** UTC ISO timestamp for scheduled activation (null = no schedule) */
  scheduled_activation_at: string | null;
  /** IANA timezone for display (e.g., "America/New_York") */
  activation_timezone: string | null;
  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
  created_by: string;
}

// Query parameters for filtering partners
export interface PartnerQueryParams {
  partner_type?: PartnerType;
  is_active?: boolean;
  search?: string; // searches partner_name, doing_business_as
  limit?: number;
  offset?: number;
}

// Paginated list response
export interface PartnerListResponse {
  partners: PartnerResponse[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

// Create partner request
export interface CreatePartnerRequest {
  partner_product_id: number;
  partner_id?: number | null;
  partner_name: string;
  doing_business_as: string;
  partner_type: PartnerType;
  program_code?: string | null;
  package_id?: string | null;
  purse_name?: string | null;
  earned_benefits?: string[];
  /** Default offers auto-assigned to new users */
  default_offers?: DefaultOfferConfig[] | null;
  /** Partner-specific content overrides for reward workflows */
  content_overrides?: PartnerContentOverrides | null;
  effective_date: string;
  expiration_date?: string | null;
  is_active?: boolean;
  notes?: string | null;
  /** Partner configuration flags */
  config?: PartnerConfigJson | null;
  /** UTC ISO timestamp for scheduled activation */
  scheduled_activation_at?: string | null;
  /** IANA timezone for display */
  activation_timezone?: string | null;
}

// Update partner request (all fields optional)
export interface UpdatePartnerRequest {
  partner_id?: number | null;
  partner_name?: string;
  doing_business_as?: string;
  partner_type?: PartnerType;
  program_code?: string | null;
  package_id?: string | null;
  purse_name?: string | null;
  earned_benefits?: string[];
  /** Default offers auto-assigned to new users */
  default_offers?: DefaultOfferConfig[] | null;
  /** Partner-specific content overrides for reward workflows */
  content_overrides?: PartnerContentOverrides | null;
  effective_date?: string;
  expiration_date?: string | null;
  is_active?: boolean;
  notes?: string | null;
  /** Partner configuration flags */
  config?: PartnerConfigJson | null;
  /** UTC ISO timestamp for scheduled activation */
  scheduled_activation_at?: string | null;
  /** IANA timezone for display */
  activation_timezone?: string | null;
}

// Partner type options for filter dropdown
export interface PartnerTypeOption {
  value: PartnerType | '';
  label: string;
}

export const PARTNER_TYPE_OPTIONS: PartnerTypeOption[] = [
  { value: '', label: 'All Types' },
  { value: 'ACO', label: 'ACO' },
  { value: 'EMPLOYER', label: 'Employer' },
  { value: 'MA', label: 'Medicare Advantage' },
];

// Status options for filter dropdown
export interface PartnerStatusOption {
  value: '' | 'true' | 'false';
  label: string;
}

export const PARTNER_STATUS_OPTIONS: PartnerStatusOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// Helper functions

export function getPartnerTypeLabel(type: PartnerType): string {
  const labels: Record<PartnerType, string> = {
    ACO: 'ACO',
    EMPLOYER: 'Employer',
    MA: 'Medicare Advantage',
  };
  return labels[type] || type;
}

export function getPartnerTypeColor(type: PartnerType): string {
  const colors: Record<PartnerType, string> = {
    ACO: 'info',
    EMPLOYER: 'default',
    MA: 'success',
  };
  return colors[type] || 'default';
}

export function getRewardKeyLabel(key: string): string {
  // Convert reward_key to human-readable label
  // Format: "some-reward-key" -> "Some Reward Key"
  const labels: Record<string, string> = {
    'benepass-card': 'Benepass Card',
    'cgm-device': 'CGM Device',
    'gift-card': 'Gift Card',
    'glp1-copay': 'GLP-1 Copay',
  };

  // Return known label or format the key nicely
  const label = labels[key];
  if (label) {
    return label;
  }

  // Fallback: Convert kebab-case to Title Case
  return key
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// =============================================================================
// Scheduled Activation — timezone options and formatting
// =============================================================================

export interface TimezoneOption {
  value: string;
  label: string;
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { value: 'America/New_York', label: 'Eastern' },
  { value: 'America/Chicago', label: 'Central' },
  { value: 'America/Denver', label: 'Mountain' },
  { value: 'America/Los_Angeles', label: 'Pacific' },
  { value: 'UTC', label: 'UTC' },
];

/**
 * Format a UTC ISO timestamp for display in the given IANA timezone.
 * Example: "Apr 15, 2026 at 9:00 AM Eastern"
 */
export function formatScheduledActivation(
  utcIso: string | null,
  timezone: string | null
): string {
  if (!utcIso) return '—';
  const tz = timezone || 'UTC';
  const tzLabel = TIMEZONE_OPTIONS.find((o) => o.value === tz)?.label || tz;
  try {
    const formatted = new Date(utcIso).toLocaleString('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${formatted} ${tzLabel}`;
  } catch {
    return utcIso;
  }
}

/**
 * Convert a datetime-local input value + IANA timezone to a UTC ISO string.
 * Uses Intl.DateTimeFormat to handle DST correctly.
 */
export function localDatetimeToUtcIso(
  datetimeLocalValue: string,
  timezone: string
): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const targetDate = new Date(datetimeLocalValue);
  const utcDate = new Date(datetimeLocalValue + 'Z');
  const parts = formatter.formatToParts(utcDate);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '0';
  const utcEquiv = new Date(
    Date.UTC(
      Number(get('year')),
      Number(get('month')) - 1,
      Number(get('day')),
      Number(get('hour')),
      Number(get('minute')),
      Number(get('second'))
    )
  );

  const offsetMs = utcEquiv.getTime() - utcDate.getTime();
  const corrected = new Date(targetDate.getTime() - offsetMs);
  return corrected.toISOString();
}

/**
 * Convert a UTC ISO string to a datetime-local input value in the given timezone.
 */
export function utcIsoToLocalDatetime(
  utcIso: string,
  timezone: string
): string {
  const date = new Date(utcIso);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}
